import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {AfriexRate, AfriexRatePromo} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {formatName} from '@/utils/formatName';
import {toTitleCase} from '@/utils/toTitleCase';

import TierUpdateButton from './TierUpdateButton';

type TierSummaryProp = {
  item: AfriexRatePromo;
  rawAppRates?: AfriexRate[];
  isLoading?: boolean;
  isDetail?: boolean;
  businessId?: string;
  businessName?: string;
  /** Called after the promo is updated so the list can refresh in place. */
  onSuccess?: () => void | Promise<void>;
};

const TierSummary = ({
  item,
  isLoading,
  rawAppRates,
  businessId,
  businessName,
  onSuccess,
}: TierSummaryProp) => {
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">{item.name}</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Tier ID</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Discount</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatValues((item?.discount || 0) * 100, 'rate', 6)}%
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Priority</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item.priority}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Promo Code
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {toTitleCase(item?.code?.replace('_', ' ') || '')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Tier Status
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {!item?.meta?.flags?.isDeactivated ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </p>
        </div>

        {item.meta?.admin && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 text-sm dark:text-slate-100">
              Approved By
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
              {formatName(item.meta?.admin)}
            </p>
          </div>
        )}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          {/* Edits go through PATCH /promos/:id, from Rates and from a business's Rates tab alike. */}
          <PermissionsProvider
            permission={AfriexAdminPermission.RatePromoManage}
          >
            <TierUpdateButton
              initialData={item}
              rawAppRates={rawAppRates}
              target={item.target}
              businessId={businessId}
              businessName={businessName}
              onSuccess={onSuccess}
            />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default TierSummary;
